import React, { useState, useEffect, useMemo, useDeferredValue } from "react"
import styles from "../../asset/styles/places/places.module.scss"
import Table, { TableColumn } from "../../component/table/table"
import utilStyles from "../../asset/styles/util/util.module.scss"
import IconsColor from "util/iconsColor"
import { agent } from "apiClient/api"
import { GuideData } from "../../models"
import moment from "moment"
import CustomizedMenu from "component/muiMenu/muiMenu"
import { useHotelGeneric } from "../../context/HotelGenericContext"
import { useSnackbar } from "../../context/snackbarContext"
import GuideEditPopup from "./guideEditPopup"
import Popup from "../../component/popup/popup"
import MainTabsTableHeader, { FilterGroup } from "../../component/mainTabsTableHeader/mainTabsTableHeader"
import { applyFilters, applySearch, cycleSort, sortRowsBy, uniqueOptionsFromRows } from "../../util/listFilters"
import { useListView, LIST_VIEW_KEYS } from "../../context/listFilterContext"
import { toTitleCase } from "util/textcase"
import { TablePaginationBar, useClientListPagination } from "../../component/tablePagination"

const Guides: React.FC = () => {
    const { showError, showSuccess } = useSnackbar()
    const [guides, setGuides] = useState<GuideData[]>([])
    const {
        search: searchValue, setSearch: setSearchValue,
        sortState, setSortState,
        filters: tableSelectedFilters, setFilters: setTableSelectedFilters,
        page, setPage, pageSize, setPageSize,
    } = useListView(LIST_VIEW_KEYS.guides)
    const handleColumnSort = React.useCallback(
        (key: string) => setSortState((prev) => cycleSort(prev, key)),
        [setSortState],
    )
    const [popupOpen, setPopupOpen] = useState(false)
    const [selectedGuide, setSelectedGuide] = useState<GuideData | null>(null)
    const [statusPopupOpen, setStatusPopupOpen] = useState(false)
    const [guideForStatus, setGuideForStatus] = useState<GuideData | null>(null)
    const { setHotelGenericData } = useHotelGeneric()

    const GetGuideList = async () => {
        try {
            const response = await agent.GuideListAPI.get()
            if (response?.status === true) {
                setGuides(response?.data ?? [])
            } else {
                showError(response?.error?.message || "Failed to load guides")
            }
        } catch (error) {
            console.log("GetGuideList Error", error)
            showError("Failed to load guides")
        }
    }

    const GetGenericHotelDetails = async () => {
        try {
            const response = await agent.GenericGlobalAPI.get()
            if (response?.status === false) {
                console.log("Failed to fetch generic hotel details", response?.error)
            } else {
                setHotelGenericData(response?.data[0])
            }
        } catch (error) {
            console.log("GetGenericHotelDetails Error", error)
        }
    }

    useEffect(() => {
        ;(async () => {
            await Promise.all([GetGuideList(), GetGenericHotelDetails()])
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const columns: TableColumn<GuideData>[] = [
        {
            key: "city_name",
            label: "City",
            width: "14%",
            align: "left",
            render: (value: string) => (
                <span className={styles.locationCell}>
                    <IconsColor iconName="location" width="16" height="16" fillColor="none" strokeColor="#7a7a7a" />
                    {toTitleCase(value) ?? "-"}
                </span>
            ),
        },
        {
            key: "base_price",
            label: "Base Price (Per Day)",
            width: "14%",
            align: "left",
            render: (value: number | null) => (value != null ? value : "-"),
        },
        {
            key: "compulsory_tipping",
            label: "Compulsory Tipping",
            width: "18%",
            align: "left",
            render: (value: number | null, row: GuideData) =>
                value != null ? `${value} (${toTitleCase(row.tipping_type_name)})` : "-",
        },
        {
            key: "flag_active",
            label: "Status",
            width: "12%",
            align: "left",
            render: (value: boolean) => (
                <span
                    className={
                        value === true
                            ? utilStyles.statusBadgeActive
                            : value === false
                              ? utilStyles.statusBadgeInactive
                              : utilStyles.statusBadgeInactive
                    }
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            key: "updated_at_timestamp",
            label: "Last Updated",
            width: "14%",
            align: "left",
            render: (value: string | null | undefined) =>
                value ? moment(value).format("DD-MMM-YYYY hh:mm A") : "-",
        },
        {
            key: "updated_by",
            label: "Editor",
            width: "12%",
            align: "left",
            render: (value: string | null | undefined) => toTitleCase(value) ?? "-",
        },
        {
            key: "actions",
            label: "",
            width: "5%",
            align: "left",
            render: (_: unknown, row: GuideData) => (
                <CustomizedMenu
                    buttonText=""
                    menuItems={[
                        {
                            label: "Edit",
                            onClick: () => {
                                setSelectedGuide(row)
                                setPopupOpen(true)
                            },
                        },
                        ...(row.guide_id
                            ? [
                                  {
                                      label: `Set Status to ${row.flag_active ? "Inactive" : "Active"}`,
                                      onClick: () => {
                                          setGuideForStatus(row)
                                          setStatusPopupOpen(true)
                                      },
                                  },
                              ]
                            : []),
                    ]}
                    buttonVariant="text"
                    buttonStyle={{ padding: "0px", minWidth: "0px" }}
                    buttonEndIcon={
                        <IconsColor iconName="moreVert" width="20" height="20" fillColor="none" strokeColor="#7a7a7a" />
                    }
                />
            ),
        },
    ]

    const getRowKey = (row: GuideData, index: number): string => {
        if (row.guide_id) return row.guide_id
        return `guide-${row.city_id}-${index}`
    }

    // ----- Filter accessors (Status, Editor per spec) -----
    const FILTER_ACCESSORS = useMemo(
        () => ({
            status: (row: GuideData) => (row.flag_active ? "Active" : "Inactive"),
            editor: (row: GuideData) => row.updated_by_user_name || "",
        }),
        [],
    )

    const filterGroups: FilterGroup[] = useMemo(
        () => [
            {
                key: "status",
                label: "Status",
                options: [
                    { label: "Active", value: "Active" },
                    { label: "Inactive", value: "Inactive" },
                ],
            },
            { key: "editor", label: "Editor", options: uniqueOptionsFromRows(guides, FILTER_ACCESSORS.editor) },
        ],
        [guides, FILTER_ACCESSORS],
    )

    // Defer heavy table re-render so clearing search/filters doesn't block the UI.
    const deferredSearch = useDeferredValue(searchValue)
    const deferredFilters = useDeferredValue(tableSelectedFilters)
    const filteredGuides = useMemo(() => {
        let rows = applyFilters(guides, deferredFilters, FILTER_ACCESSORS)
        rows = applySearch(rows, deferredSearch, [(r) => r.city_name])
        rows = sortRowsBy(rows, sortState)
        return rows
    }, [guides, deferredFilters, deferredSearch, sortState, FILTER_ACCESSORS])

    const paginationQueryKey = JSON.stringify({ search: searchValue, filters: tableSelectedFilters, sortState })
    const {
        pagedRows: pagedGuides,
        total: pagedTotal,
        page: currentPage,
        pageSize: currentPageSize,
        handlePageChange,
        handlePageSizeChange,
    } = useClientListPagination(filteredGuides, {
        page, pageSize, setPage, setPageSize, queryKey: paginationQueryKey,
    })

    const handleConfirmStatusChange = async () => {
        const guideToUpdate = guideForStatus
        setStatusPopupOpen(false)
        setGuideForStatus(null)
        if (!guideToUpdate?.guide_id) return
        try {
            const response = await agent.GuideDeleteAPI.put({
                guide_id: guideToUpdate.guide_id,
                flag_active: !guideToUpdate.flag_active,
                guide_flag_deleted: false,
            })
            if (response?.status === true) {
                showSuccess("Guide status updated successfully")
                await GetGuideList()
            } else {
                showError(response?.error?.message ?? "Failed to update guide status")
            }
        } catch (err) {
            console.error("Update guide status error", err)
            showError("Failed to update guide status")
        }
    }

    return (
        <div className={`${utilStyles.pageContainer} ${utilStyles.listPageFill}`}>
            <div className={utilStyles.listPageHeader}>
                    <MainTabsTableHeader
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        searchPlaceholder="Search guides"
                        totalCount={guides.length}
                        filteredCount={filteredGuides.length}
                        itemLabel="guides"
                        sortValue=""
                        onSortChange={() => undefined}
                        sortOptions={[]}
                        filterGroups={filterGroups}
                        selectedFilters={tableSelectedFilters}
                        onFiltersChange={setTableSelectedFilters}
                        addButtonLabel=""
                        onAddClick={() => undefined}
                        showAddButton={false}
                        showResultCount={false}
                    />
            </div>
            <div className={utilStyles.listPageTableBlock}>
                <div className={utilStyles.listPageTableScroll}>
                    <Table
                        columns={columns}
                        data={pagedGuides}
                        keyExtractor={getRowKey}
                        emptyMessage="No guides available"
                        minWidth={1000}
                        showHeader={true}
                        fillHeight={true}
                        sortKey={sortState.key}
                        sortDirection={sortState.direction}
                        onSort={handleColumnSort}
                    />
                </div>
                <TablePaginationBar
                    total={pagedTotal}
                    page={currentPage}
                    pageSize={currentPageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>
            <GuideEditPopup
                isOpen={popupOpen}
                onClose={() => {
                    setPopupOpen(false)
                    setSelectedGuide(null)
                }}
                guide={selectedGuide}
                onSuccess={GetGuideList}
            />
            <Popup
                isOpen={statusPopupOpen}
                onClose={() => {
                    setStatusPopupOpen(false)
                    setGuideForStatus(null)
                }}
                title="Change Guide Status"
                primaryButton={{ label: "Confirm", onClick: handleConfirmStatusChange }}
                secondaryButton={{
                    label: "Cancel",
                    onClick: () => {
                        setStatusPopupOpen(false)
                        setGuideForStatus(null)
                    },
                }}
                heightFiexd={true}
            >
                {guideForStatus && (
                    <div className={styles.confirmationContent}>
                        <IconsColor
                            iconName="warningSign"
                            width="80px"
                            height="80px"
                            fillColor="none"
                            strokeColor="#FF0000"
                        />
                        <p className={styles.confirmationText}>
                            Are you sure you want to change the status of{" "}
                            <strong>{guideForStatus.city_name}</strong> to{" "}
                            <strong>{guideForStatus.flag_active ? "Inactive" : "Active"}</strong>?
                        </p>
                    </div>
                )}
            </Popup>
        </div>
    )
}

export default Guides
